/**
 * Provider profile form helpers — hydrate/serialize Edit Partner tabs.
 */

import {
  normalizeProvinceCodes,
  normalizeServiceValues,
  normalizeRouteCodes,
  normalizeVehicleTypes,
  normalizeStringList,
} from "../constants/providerProfileOptions.js";

export const createEmptyProviderProfile = () => ({
  businessProfile: {
    tradingName: "",
    businessType: "",
    registrationNumber: "",
    vatNumber: "",
    yearsInBusiness: "",
    description: "",
  },
  serviceProfile: {
    services: [],
    vehicleTypes: [],
    notes: "",
  },
  fleetSummary: {
    vehicleCount: "",
    capacityNotes: "",
    hasTailLift: false,
    hasBlankets: false,
    hasGps: false,
    notes: "",
  },
  coverage: {
    provinces: [],
    cities: [],
    routes: [],
    serviceRadiusKm: "",
    longDistanceCapable: false,
  },
  complianceSummary: {
    status: "pending",
    documentsReceived: "",
  },
  commercialSummary: {
    paymentTerms: "",
    settlementPreference: "",
    typicalPricingNotes: "",
    bankReady: false,
    notes: "",
  },
});

export const createEmptyBasicFields = () => ({
  name: "",
  contactPerson: "",
  phone: "",
  phoneIsWhatsApp: false,
  email: "",
  address: "",
});

/** Provider payout bank details — persisted under contractDetails.bank */
export const createEmptyBankFields = () => ({
  bankName: "",
  accountHolder: "",
  accountNumber: "",
  branchCode: "",
});

const splitCsv = (value) => normalizeStringList(value);

/**
 * Hydrate bank form fields from partner.contractDetails.bank (or .banking).
 * PII: callers must not log accountNumber.
 */
export const bankFromPartner = (partner) => {
  const bank =
    partner?.contractDetails?.bank || partner?.contractDetails?.banking || {};
  return {
    bankName: bank.bankName || bank.name || "",
    accountHolder: bank.accountHolder || "",
    accountNumber: bank.accountNumber || "",
    branchCode: bank.branchCode || "",
  };
};

export const hasBankDetails = (bankOrPartner) => {
  const bank =
    bankOrPartner?.contractDetails?.bank ||
    bankOrPartner?.contractDetails?.banking ||
    bankOrPartner?.bank ||
    bankOrPartner ||
    {};
  return Boolean(
    bank.bankName ||
      bank.name ||
      bank.accountHolder ||
      bank.accountNumber ||
      bank.branchCode
  );
};

/** Mask account number for display summaries (keep last 4). */
export const maskAccountNumber = (accountNumber) => {
  const digits = String(accountNumber || "").replace(/\s+/g, "");
  if (!digits) return "";
  if (digits.length <= 4) return "****";
  return `****${digits.slice(-4)}`;
};

/**
 * Merge bank form into existing contractDetails without dropping other keys.
 * Empty bank fields remove contractDetails.bank.
 */
export const buildContractDetailsWithBank = (existingContractDetails, bankForm) => {
  const next = { ...(existingContractDetails || {}) };
  const bank = {
    bankName: String(bankForm?.bankName || "").trim(),
    accountHolder: String(bankForm?.accountHolder || "").trim(),
    accountNumber: String(bankForm?.accountNumber || "").trim(),
    branchCode: String(bankForm?.branchCode || "").trim(),
  };
  if (Object.values(bank).some(Boolean)) {
    next.bank = bank;
  } else {
    delete next.bank;
  }
  return next;
};

export const profileFromPartner = (partner) => {
  const emptyProviderProfile = createEmptyProviderProfile();
  const profile = partner?.providerProfile || {};
  return {
    businessProfile: {
      ...emptyProviderProfile.businessProfile,
      ...profile.businessProfile,
      yearsInBusiness: profile.businessProfile?.yearsInBusiness ?? "",
    },
    serviceProfile: {
      services: normalizeServiceValues(profile.serviceProfile?.services),
      vehicleTypes: normalizeVehicleTypes(profile.serviceProfile?.vehicleTypes),
      notes: profile.serviceProfile?.notes || "",
    },
    fleetSummary: {
      vehicleCount: profile.fleetSummary?.vehicleCount ?? "",
      capacityNotes: profile.fleetSummary?.capacityNotes || "",
      hasTailLift: profile.fleetSummary?.hasTailLift === true,
      hasBlankets: profile.fleetSummary?.hasBlankets === true,
      hasGps: profile.fleetSummary?.hasGps === true,
      notes: profile.fleetSummary?.notes || "",
    },
    coverage: {
      provinces: normalizeProvinceCodes(profile.coverage?.provinces),
      cities: normalizeStringList(profile.coverage?.cities),
      routes: normalizeRouteCodes(profile.coverage?.routes),
      serviceRadiusKm: profile.coverage?.serviceRadiusKm ?? "",
      longDistanceCapable: profile.coverage?.longDistanceCapable === true,
    },
    complianceSummary: {
      status: profile.complianceSummary?.status || "pending",
      documentsReceived: splitCsv(profile.complianceSummary?.documentsReceived),
    },
    commercialSummary: {
      paymentTerms: profile.commercialSummary?.paymentTerms || "",
      settlementPreference: profile.commercialSummary?.settlementPreference || "",
      typicalPricingNotes: profile.commercialSummary?.typicalPricingNotes || "",
      bankReady: profile.commercialSummary?.bankReady === true,
      notes: profile.commercialSummary?.notes || "",
    },
  };
};

export const basicFieldsFromPartner = (partner) => ({
  name: partner?.name || "",
  contactPerson: partner?.contactPerson || "",
  phone: partner?.phone || "",
  phoneIsWhatsApp: partner?.phoneIsWhatsApp === true,
  email: partner?.email || "",
  address: partner?.address || "",
});

export const buildProviderProfilePayload = (profileForm) => ({
  businessProfile: {
    ...profileForm.businessProfile,
    yearsInBusiness: profileForm.businessProfile.yearsInBusiness
      ? Number(profileForm.businessProfile.yearsInBusiness)
      : null,
  },
  serviceProfile: {
    services: profileForm.serviceProfile.services,
    vehicleTypes: profileForm.serviceProfile.vehicleTypes,
    notes: profileForm.serviceProfile.notes,
  },
  fleetSummary: {
    vehicleCount: profileForm.fleetSummary.vehicleCount
      ? Number(profileForm.fleetSummary.vehicleCount)
      : null,
    vehicleTypes: profileForm.serviceProfile.vehicleTypes,
    capacityNotes: profileForm.fleetSummary.capacityNotes,
    hasTailLift: profileForm.fleetSummary.hasTailLift,
    hasBlankets: profileForm.fleetSummary.hasBlankets,
    hasGps: profileForm.fleetSummary.hasGps,
    notes: profileForm.fleetSummary.notes,
  },
  coverage: {
    provinces: profileForm.coverage.provinces,
    cities: profileForm.coverage.cities,
    routes: profileForm.coverage.routes,
    serviceRadiusKm: profileForm.coverage.serviceRadiusKm
      ? Number(profileForm.coverage.serviceRadiusKm)
      : null,
    longDistanceCapable: profileForm.coverage.longDistanceCapable,
  },
  complianceSummary: {
    status: profileForm.complianceSummary.status,
    documentsReceived: splitCsv(profileForm.complianceSummary.documentsReceived),
  },
  commercialSummary: {
    ...profileForm.commercialSummary,
  },
});
